import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert';
import { DashboardLayout } from '../common/page_component/index.js';
import externalCommissionService from '../../services/ExternalCommissionService.js';
import { mlSheetService } from '../../services/mlSheetService';
import Button from '../common/Button.js';
import { DollarSign, ArrowLeft } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';


const AddExternalCommission = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mlSheets, setMlSheets] = useState([]);
  const [owners, setOwners] = useState([]);
  const [form, setForm] = useState({ ml_sheet_id: '', vehicle_owner_id: '', remarks: '' });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  const ML_PAGE_SIZE = 5;
  const [mlSearchQuery, setMlSearchQuery] = useState('');
  const [mlDropdownOpen, setMlDropdownOpen] = useState(false);
  const [mlPage, setMlPage] = useState(1);
  const [mlListLoading, setMlListLoading] = useState(false);
  const [mlHasMore, setMlHasMore] = useState(true);
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false);
  const [ownerSearchQuery, setOwnerSearchQuery] = useState('');

  const fetchMlSheetsPage = async (page, search) => {
    setMlListLoading(true);
    try {
      const res = await mlSheetService.getAllMLSheets(page, ML_PAGE_SIZE, {
        assignment_scope: 'EXTERNAL',
        ...(search ? { search } : {}),
      });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      const externalOnly = list.filter((m) => m.assignment_scope === 'EXTERNAL');
      setMlSheets(prev => page === 1 ? externalOnly : [...prev, ...externalOnly]);
      const pagination = res.data?.pagination;
      setMlHasMore(pagination ? page < pagination.pages : externalOnly.length === ML_PAGE_SIZE);
    } catch (err) {
      Alert.error('Failed to load ML Sheets');
    } finally {
      setMlListLoading(false);
    }
  };

  useEffect(() => {
    setDataLoading(true);
    Promise.all([
      fetchMlSheetsPage(1, ''),
      externalCommissionService.getVehicleOwners(),
    ]).then(([, ownerRes]) => {
      const oData = ownerRes?.data?.data || ownerRes?.data || [];
      setOwners(Array.isArray(oData) ? oData : []);
    }).catch(() => Alert.error('Failed to load form data'))
      .finally(() => setDataLoading(false));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.ml-sheet-dropdown')) setMlDropdownOpen(false);
      if (!e.target.closest('.owner-dropdown')) setOwnerDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!form.ml_sheet_id || !form.vehicle_owner_id) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    externalCommissionService.calculatePreview(form.ml_sheet_id, form.vehicle_owner_id)
      .then((res) => setPreview(res.data.data))
      .catch((err) => {
        setPreview(null);
        Alert.error(err.response?.data?.error || 'Preview failed');
      })
      .finally(() => setPreviewLoading(false));
  }, [form.ml_sheet_id, form.vehicle_owner_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ml_sheet_id || !form.vehicle_owner_id) {
      Alert.error('Select ML Sheet and Vehicle Owner');
      return;
    }
    setLoading(true);
    try {
      await externalCommissionService.create(form);
      Alert.success('Vehicle owner commission created');
      navigate('/external-commissions');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Create failed');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

  const handleMlSelect = (sheetId) => {
    const selectedSheet = mlSheets.find((s) => s.ml_sheet_id === sheetId);
    const matchedOwner = selectedSheet
      ? owners.find((o) => Array.isArray(o.vehicles) && o.vehicles.some((vid) => String(vid) === String(selectedSheet.vehicle_id)))
      : null;

    setForm(prev => ({
      ...prev,
      ml_sheet_id: sheetId,
      vehicle_owner_id: matchedOwner ? matchedOwner.vehicle_owner_id : '',
    }));
    setMlDropdownOpen(false);
    setMlSearchQuery('');
    setMlPage(1);
  };

  const handleMlScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 10 && mlHasMore && !mlListLoading) {
      const next = mlPage + 1;
      setMlPage(next);
      fetchMlSheetsPage(next, mlSearchQuery);
    }
  };

  const handleMlSearchChange = (v) => {
    setMlSearchQuery(v);
    setMlPage(1);
    fetchMlSheetsPage(1, v);
  };

  const selectedMlSheet = form.ml_sheet_id
    ? mlSheets.find((s) => s.ml_sheet_id === form.ml_sheet_id)
    : null;
  const selectedMlFromNames = selectedMlSheet ? (selectedMlSheet.from_locations || []).map(l => l.location).filter(Boolean).join(', ') : '';
  const selectedMlToNames = selectedMlSheet ? (selectedMlSheet.to_locations || []).map(l => l.location).filter(Boolean).join(', ') : '';

  const filteredOwners = owners.filter((o) => {
    const q = ownerSearchQuery.trim().toLowerCase();
    if (!q) return true;
    return (o.owner_name || '').toLowerCase().includes(q) ||
      (o.contact || '').toLowerCase().includes(q) ||
      (o.email || '').toLowerCase().includes(q);
  });

  const selectedOwner = form.vehicle_owner_id
    ? owners.find((o) => o.vehicle_owner_id === form.vehicle_owner_id)
    : null;

  const handleOwnerSelect = (ownerId) => {
    setForm(prev => ({ ...prev, vehicle_owner_id: ownerId }));
    setOwnerDropdownOpen(false);
    setOwnerSearchQuery('');
  };

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4">
          <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
            <DollarSign className="w-5 h-5 text-secondary" /> Add Vehicle Owner Commission
          </h1>
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/external-commissions')} className="w-full sm:w-auto">
            Back
          </Button>
        </div>

        {dataLoading ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center text-sm text-gray-500">Loading form data...</div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
            <div>
              <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 1: Select Shipment</h3>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">ML Sheet ID <span className="text-danger font-bold">*</span></label>
                <div className="relative ml-sheet-dropdown">
                  <div className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl text-sm sm:text-base cursor-pointer bg-white flex justify-between items-center"
                    onClick={() => setMlDropdownOpen(p => !p)}>
                    <div className="flex flex-col min-w-0">
                      <span className={form.ml_sheet_id ? 'text-gray-900' : 'text-gray-400'}>{form.ml_sheet_id || 'Select ML Sheet'}</span>
                      {selectedMlSheet && (selectedMlFromNames || selectedMlToNames || selectedMlSheet.vehicle_number) && (
                        <span className="text-[11px] text-gray-500 font-normal truncate">
                          {selectedMlFromNames && selectedMlToNames ? `${selectedMlFromNames} → ${selectedMlToNames}` : (selectedMlFromNames || selectedMlToNames)}
                          {selectedMlSheet.vehicle_number && <span className="ml-1 text-gray-400">• {selectedMlSheet.vehicle_number}</span>}
                        </span>
                      )}
                    </div>
                    <span className="text-gray-400 text-xs">▼</span>
                  </div>
                  {mlDropdownOpen && (
                    <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1">
                      <div className="p-2 border-b border-gray-200">
                        <input type="text" placeholder="Search ML Sheet..." value={mlSearchQuery} onChange={(e) => handleMlSearchChange(e.target.value)}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" autoFocus />
                      </div>
                      <div className="max-h-48 overflow-y-auto" onScroll={handleMlScroll}>
                        {mlSheets.length > 0 ? mlSheets.map(s => {
                          const fromNames = (s.from_locations || []).map(l => l.location).filter(Boolean).join(', ');
                          const toNames = (s.to_locations || []).map(l => l.location).filter(Boolean).join(', ');
                          const createdDate = s.created_at ? new Date(s.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                          return (
                            <div key={s.ml_sheet_id} onClick={() => handleMlSelect(s.ml_sheet_id)}
                              className={`px-4 py-2 text-sm cursor-pointer hover:bg-orange-50 hover:text-orange-700 ${form.ml_sheet_id === s.ml_sheet_id ? 'bg-orange-100 font-semibold' : ''}`}>
                              <div className="flex justify-between items-center gap-2">
                                <span>{s.ml_sheet_id}</span>
                                {createdDate && <span className="text-[11px] text-gray-400 font-normal whitespace-nowrap">{createdDate}</span>}
                              </div>
                              {(fromNames || toNames || s.vehicle_number) && (
                                <div className="text-[11px] text-gray-500 font-normal mt-0.5 truncate">
                                  {fromNames && toNames ? `${fromNames} → ${toNames}` : (fromNames || toNames)}
                                  {s.vehicle_number && <span className="ml-1 text-gray-400">• {s.vehicle_number}</span>}
                                </div>
                              )}
                            </div>
                          );
                        }) : <div className="px-4 py-3 text-sm text-gray-400">No ML Sheets found</div>}
                        {mlListLoading && <div className="px-4 py-2 text-xs text-center text-gray-400 border-t border-gray-100">Loading...</div>}
                        {!mlListLoading && mlHasMore && <div className="px-4 py-2 text-xs text-center text-gray-400 border-t border-gray-100">Scroll for more...</div>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 2: Select Vehicle Owner</h3>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Vehicle Owner <span className="text-danger font-bold">*</span></label>
                <div className="relative owner-dropdown">
                  <div className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl text-sm sm:text-base cursor-pointer bg-white flex justify-between items-center"
                    onClick={() => setOwnerDropdownOpen(p => !p)}>
                    <div className="flex flex-col min-w-0">
                      <span className={form.vehicle_owner_id ? 'text-gray-900' : 'text-gray-400'}>
                        {selectedOwner ? selectedOwner.owner_name : (form.vehicle_owner_id || 'Select Vehicle Owner')}
                      </span>
                      {selectedOwner && (
                        <span className="text-[11px] text-gray-400 font-normal truncate">
                          {selectedOwner.contact}{selectedOwner.contact && selectedOwner.email ? ' • ' : ''}{selectedOwner.email}
                          {(selectedOwner.contact || selectedOwner.email) ? ' · ' : ''}
                          {(selectedOwner.vehicles || []).length} vehicle{(selectedOwner.vehicles || []).length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <span className="text-gray-400 text-xs">▼</span>
                  </div>
                  {ownerDropdownOpen && (
                    <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1">
                      <div className="p-2 border-b border-gray-200">
                        <input type="text" placeholder="Search Vehicle Owner..." value={ownerSearchQuery} onChange={(e) => setOwnerSearchQuery(e.target.value)}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" autoFocus />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredOwners.length > 0 ? filteredOwners.map((o) => (
                          <div key={o.vehicle_owner_id} onClick={() => handleOwnerSelect(o.vehicle_owner_id)}
                            className={`px-4 py-2 text-sm cursor-pointer hover:bg-orange-50 hover:text-orange-700 ${form.vehicle_owner_id === o.vehicle_owner_id ? 'bg-orange-100 font-semibold' : ''}`}>
                            <div className="flex justify-between items-center gap-2">
                              <span>{o.owner_name}</span>
                              <span className="text-[11px] text-gray-400 font-normal whitespace-nowrap">{(o.vehicles || []).length} vehicle{(o.vehicles || []).length !== 1 ? 's' : ''}</span>
                            </div>
                            {(o.contact || o.email) && (
                              <div className="text-[11px] text-gray-500 font-normal mt-0.5 truncate">
                                {o.contact}{o.contact && o.email ? ' • ' : ''}{o.email}
                              </div>
                            )}
                          </div>
                        )) : <div className="px-4 py-3 text-sm text-gray-400">No vehicle owners found</div>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Remarks</label>
              <textarea
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                rows={2}
              />
            </div>

            {previewLoading && <p className="text-sm text-gray-500">Calculating preview...</p>}
            {preview && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 sm:p-6 text-sm space-y-2">
                <p className="font-semibold text-gray-800 text-base">Calculation Preview</p>
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-3">
                  <span className="text-gray-600">External Shipments:</span><span className="text-right font-medium">{preview.shipment_count}</span>
                  <span className="text-gray-600">Total Freight:</span><span className="text-right font-medium">{fmt(preview.total_freight)}</span>
                  <span className="text-gray-600">Paid at Origin:</span><span className="text-right font-medium">{fmt(preview.paid_freight)}</span>
                  <span className="text-gray-600">To Pay at Destination:</span><span className="text-right font-semibold">{fmt(preview.balance_freight)}</span>
                  <span className="text-gray-600">Agent Commission:</span><span className="text-right font-medium text-purple-700">{fmt(preview.total_agent_commission ?? preview.company_share_on_balance)}</span>
                  <span className="text-gray-600">Vehicle Owner Share:</span><span className="text-right font-medium text-green-700">{fmt(preview.total_owner_share ?? preview.owner_commission_amount)}</span>
                  <span className="text-gray-600 font-medium">Receive from Agent:</span>
                  <span className="text-right font-bold text-orange-700">{fmt(preview.total_agent_remit_due ?? preview.net_receivable_from_owner)}</span>
                  <span className="text-gray-600 font-medium">Pay to Owner (to-pay share):</span>
                  <span className="text-right font-bold text-blue-700">{fmt(preview.total_owner_share_due ?? preview.net_payable_to_owner)}</span>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
              <Button type="submit" variant="primary" disabled={loading || !preview} loading={loading} className="w-full sm:w-auto">
                Create Vehicle Owner Commission
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate('/external-commissions')} className="w-full sm:w-auto">
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AddExternalCommission;
